import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';

import { Subject } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { GlobalService } from '../services/global.service';
import { environment } from 'src/environments/environment';

// 🔧 FEATURE FLAGS - Funções auxiliares
function skipAuthInDev(): boolean {
  return (environment as any).features?.skipAuthInDev || false;
}

@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    public globalService: GlobalService,
    private authService: AuthService,
    private router: Router,
  ) {}

  canActivate(next: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean {
    // 🔧 DEV MODE: Bypass autenticação (configurado em environment.ts)
    if (skipAuthInDev()) {
      return true;
    }

    // ✅ OPTIMIZADO: Verificação síncrona directa, sem esperar por isRestoringSession$.
    // O BehaviorSubject causava bloqueio desnecessário nas navegações entre áreas,
    // porque ao trocar de módulo (ex: CRM → ClientArea), o guard ficava à espera
    // do pipe(filter, take) antes de avaliar permissões.
    // A sessão já foi restaurada no AppComponent.initializeSession() antes de chegar aqui.
    if (!this.authService.isLoggedin(this.globalService.loggedUserClass)) {
      // Tentar restaurar como último recurso
      const restoredUser = this.authService.restoreSession();
      if (restoredUser) {
        this.globalService.loggedUserClass = restoredUser;
      } else {
        this.authService.logout(false, '', false);
        this.router.navigate(['/login'], { queryParams: { returnUrl: _state.url } });
        return false;
      }
    }

    // Verificar permissão específica
    return this.checkPermission(next.data);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.canActivate(route, state);
  }

  private checkPermission(data: any): boolean {
    const user = this.globalService.loggedUserClass;

    // All = qualquer utilizador autenticado pode aceder
    if (data.All || data.All === 1 || data.All === '1') {
      return true;
    }

    // Admin tem acesso a tudo
    if (user?.Admin) {
      return true;
    }

    // Verificar permissão específica da rota
    if (data.ClientArea === 1 && user?.ClientArea) return true;
    if (data.CommercialArea === 1 && user?.CommercialArea) return true;
    if (data.IntermediaryArea === 1 && user?.IntermediaryArea) return true;
    if (data.SiteManagement === 1 && user?.SiteManagement) return true;

    // Sem permissão - redireciona para login
    this.authService.logout(false, '', false);
    this.router.navigate(['/login']);
    return false;
  }

  ngOnDestroy() {
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }
}
